import { formatMoney } from "../../services/currency";
﻿import React, {
  FormEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getAllInventory, IInventory } from "../../services/inventoryApi";
import {
  AuctionBoxPreset,
  AuctionBuyer,
  AuctionBuyerDetail,
  AuctionPackage,
  AuctionSession,
  AuctionWin,
  SessionDetail,
  WinStatus,
  liveAuctionsApi,
} from "../../services/liveAuctionsApi";
import { useToast } from "../../components/Toast/ToastContext";
import {
  BuyerGroup,
  groupWinsByBuyer,
  InventoryMap,
  lineProfitBeforeBoxCosts,
  packageSellerCovered,
  packageUnits,
  summarizeBuyerGroup,
  summarizeWins,
  applyCurrentInventoryCost,
  toCents,
} from "./reporting";
import ReportsPage from "../Reports/ReportsPage";

export const formatAuctionMoney = (cents = 0) => formatMoney(cents / 100);
const localDate = (date = new Date()) =>
  new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
const localDateOnly = (date = new Date()) => localDate(date).slice(0, 10);
const localDay = (date = new Date()) => localDate(date).slice(0, 10);
const isPastLocalDay = (value: string) => value.slice(0, 10) < localDay();
const message = (e: any) =>
  e?.response?.data?.message || e?.message || "Something went wrong.";
const submissionId = () =>
  globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
const normalizeUsername = (value: string) =>
  value.trim().replace(/^@+/, "").toLocaleLowerCase("en-US");
const input =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100";
const primary =
  "rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50";
const inferHistoricalMode = (session: AuctionSession) =>
  session.historicalImportDefault === true ||
  session.status !== "ACTIVE" ||
  isPastLocalDay(localDate(new Date(session.startedAt)));
const parseAuctionNameDate = (dateValue: string) => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    const [year, month, day] = dateValue.split("-").map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(dateValue);
};
const defaultAuctionName = (dateValue = localDateOnly()) =>
  `TikTok LIVE — ${parseAuctionNameDate(dateValue).toLocaleDateString("en-US")}`;
const localDateOnlyToIso = (value: string) =>
  new Date(`${value}T00:00:00`).toISOString();

function Metric({
  label,
  value,
  compact = false,
  valueClassName = "",
}: {
  label: string;
  value: string | number;
  compact?: boolean;
  valueClassName?: string;
}) {
  return (
    <div
      className={`rounded-xl border border-slate-200 bg-white shadow-sm ${compact ? "p-3" : "p-4"}`}
    >
      <div className="text-xs font-bold uppercase text-slate-500">{label}</div>
      <div
        className={`mt-1 font-bold text-slate-900 ${compact ? "text-lg" : "text-xl"} ${valueClassName}`}
      >
        {value}
      </div>
    </div>
  );
}

type PackageDraft = {
  boxSizeLabel: string;
  packaging: string;
  shipping: string;
  income: string;
  credit: string;
  order: string;
  tracking: string;
};

const emptyPackageDraft = (): PackageDraft => ({
  boxSizeLabel: "",
  packaging: "",
  shipping: "",
  income: "",
  credit: "",
  order: "",
  tracking: "",
});

const packagePayload = (
  sessionId: string,
  buyerId: string,
  winIds: string[],
  draft: PackageDraft,
) => ({
  boxSizeLabel: draft.boxSizeLabel.trim() || undefined,
  auctionSessionId: sessionId,
  auctionBuyerId: buyerId,
  auctionWinIds: winIds,
  packagingCost: Number(draft.packaging || 0),
  actualShippingCost: Number(draft.shipping || 0),
  customerPaidShipping: Number(draft.income || 0),
  platformShippingCredit: Number(draft.credit || 0),
  tiktokOrderId: draft.order,
  trackingNumber: draft.tracking,
});

const persistAuctionPackage = (
  sessionId: string,
  buyerId: string,
  winIds: string[],
  draft: PackageDraft,
  packageId?: string | null,
) => {
  const body = packagePayload(sessionId, buyerId, winIds, draft);
  return packageId
    ? liveAuctionsApi.updatePackage(packageId, body)
    : liveAuctionsApi.createPackage(body);
};

export default function LiveAuctionsPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { notify } = useToast();
  const [sessions, setSessions] = useState<AuctionSession[]>([]),
    [buyers, setBuyers] = useState<AuctionBuyer[]>([]),
    [inventory, setInventory] = useState<IInventory[]>([]);
  const [detail, setDetail] = useState<SessionDetail | null>(null),
    [tab, setTab] = useState<"sessions" | "buyers" | "reports">("sessions"),
    [workspace, setWorkspace] = useState<"capture" | "reconcile" | "presets">(
      "capture",
    );
  const [loading, setLoading] = useState(true),
    [error, setError] = useState(""),
    [creating, setCreating] = useState(false);
  const loadBase = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [s, b, i] = await Promise.all([
        liveAuctionsApi.sessions(),
        liveAuctionsApi.buyers(),
        getAllInventory().then((r) => r.data.items),
      ]);
      setSessions(s);
      setBuyers(b);
      setInventory(i);
    } catch (e) {
      setError(message(e));
    } finally {
      setLoading(false);
    }
  }, []);
  const loadDetail = useCallback(async () => {
    if (!sessionId) {
      setDetail(null);
      return;
    }
    try {
      setDetail(await liveAuctionsApi.session(sessionId));
    } catch (e) {
      setError(message(e));
    }
  }, [sessionId]);
  useEffect(() => {
    void loadBase();
  }, [loadBase]);
  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);
  const refresh = async () => {
    await Promise.all([loadBase(), loadDetail()]);
  };
  if (sessionId && detail)
    return (
      <Workspace
        detail={detail}
        inventory={inventory}
        buyers={buyers}
        tab={workspace}
        setTab={setWorkspace}
        refresh={refresh}
        back={() => navigate("/live-auctions")}
        notify={notify}
      />
    );
  return (
    <main className="min-h-[calc(100vh-56px)] bg-slate-50 px-4 py-8 md:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="flex items-end justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-violet-600">
              TikTok selling
            </p>
            <h1 className="text-3xl font-bold">Live Auctions</h1>
            <p className="text-sm text-slate-500">
              Capture wins fast, then reconcile payments and packages.
            </p>
          </div>
          <button className={primary} onClick={() => setCreating((v) => !v)}>
            + Create auction
          </button>
        </div>
        <div className="mt-6 flex gap-2 border-b">
          <button
            className={`px-4 py-3 font-semibold ${tab === "sessions" ? "border-b-2 border-blue-600 text-blue-700" : "text-slate-500"}`}
            onClick={() => setTab("sessions")}
          >
            Sessions
          </button>
          <button
            className={`px-4 py-3 font-semibold ${tab === "buyers" ? "border-b-2 border-blue-600 text-blue-700" : "text-slate-500"}`}
            onClick={() => setTab("buyers")}
          >
            Buyers
          </button>
          <button
            className={`px-4 py-3 font-semibold ${tab === "reports" ? "border-b-2 border-blue-600 text-blue-700" : "text-slate-500"}`}
            onClick={() => setTab("reports")}
          >
            Reports
          </button>
        </div>
        {creating && (
          <CreateSession
            notify={notify}
            done={async (row) => {
              setCreating(false);
              await refresh();
              navigate(`/live-auctions/${row._id}`);
            }}
          />
        )}
        {error && (
          <p
            role="alert"
            className="mt-5 rounded-lg bg-red-50 p-3 text-red-700"
          >
            {error}
          </p>
        )}
        {loading ? (
          <p className="mt-8 text-slate-500">Loading...</p>
        ) : tab === "sessions" ? (
          <SessionList
            sessions={sessions}
            open={(id) => navigate(`/live-auctions/${id}`)}
          />
        ) : tab === "reports" ? (
          <ReportsPage embedded />
        ) : (
          <Buyers
            buyers={buyers}
            sessions={sessions}
            inventory={inventory}
            refresh={refresh}
            notify={notify}
            addPrevious={(id, buyer) =>
              navigate(
                `/live-auctions/${id}?buyer=${encodeURIComponent(buyer)}`,
              )
            }
          />
        )}
      </div>
    </main>
  );
}

function CreateSession({
  done,
  notify,
}: {
  done: (s: AuctionSession) => void;
  notify: any;
}) {
  const [name, setName] = useState(() => defaultAuctionName()),
    [date, setDate] = useState(localDateOnly()),
    [fee, setFee] = useState("3"),
    [historical, setHistorical] = useState(false),
    [nameCustomized, setNameCustomized] = useState(false),
    [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!nameCustomized) {
      setName(defaultAuctionName(date));
    }
  }, [date, nameCustomized]);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      const row = await liveAuctionsApi.createSession({
        name,
        startedAt: localDateOnlyToIso(date),
        platform: "TIKTOK",
        platformFeeRate: Number(fee),
        status: historical ? "RECONCILING" : "ACTIVE",
        historicalImportDefault: historical,
      });
      notify("Auction created.", { type: "success" });
      done(row);
    } catch (err) {
      notify(message(err), { type: "error" });
    } finally {
      setSaving(false);
    }
  };
  return (
    <form
      onSubmit={submit}
      className="mt-6 grid gap-4 rounded-xl border border-blue-200 bg-blue-50 p-5 md:grid-cols-4"
    >
      <label className="text-sm font-semibold md:col-span-2">
        Name
        <input
          required
          className={`${input} mt-1`}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setNameCustomized(
              e.target.value.trim() !== defaultAuctionName(date).trim(),
            );
          }}
        />
      </label>
      <label className="text-sm font-semibold">
        Original date
        <input
          required
          type="date"
          className={`${input} mt-1`}
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
      </label>
      <label className="text-sm font-semibold">
        Platform fee %
        <input
          required
          type="number"
          min="0"
          max="100"
          step=".01"
          className={`${input} mt-1`}
          value={fee}
          onChange={(e) => setFee(e.target.value)}
        />
      </label>
      <label className="flex items-center gap-2 text-sm md:col-span-3">
        <input
          type="checkbox"
          checked={historical}
          onChange={(e) => setHistorical(e.target.checked)}
        />{" "}
        Historical/backfill session (no inventory adjustment by default)
      </label>
      <button disabled={saving} className={primary}>
        Create and open
      </button>
    </form>
  );
}

function SessionList({
  sessions,
  open,
}: {
  sessions: AuctionSession[];
  open: (id: string) => void;
}) {
  const [shipmentSummaryBySession, setShipmentSummaryBySession] = useState<
    Record<string, { active: number; shipped: number; delivered: number }>
  >({});
  useEffect(() => {
    let cancelled = false;
    const loadShipmentSummaries = async () => {
      const rows = await Promise.all(
        sessions.map(async (session) => {
          try {
            const detail = await liveAuctionsApi.session(session._id);
            const activePackages = detail.packages.filter(
              (pkg) => pkg.status !== "CANCELLED",
            );
            const shippedPackages = activePackages.filter((pkg) =>
              ["SHIPPED", "DELIVERED"].includes(pkg.status),
            );
            const deliveredPackages = activePackages.filter(
              (pkg) => pkg.status === "DELIVERED",
            );
            return [
              session._id,
              {
                active: activePackages.length,
                shipped: shippedPackages.length,
                delivered: deliveredPackages.length,
              },
            ] as const;
          } catch {
            return [
              session._id,
              { active: 0, shipped: 0, delivered: 0 },
            ] as const;
          }
        }),
      );
      if (!cancelled) {
        setShipmentSummaryBySession(Object.fromEntries(rows));
      }
    };
    void loadShipmentSummaries();
    return () => {
      cancelled = true;
    };
  }, [sessions]);
  if (!sessions.length)
    return (
      <div className="mt-7 rounded-xl border border-dashed bg-white p-10 text-center text-slate-500">
        No auction sessions yet.
      </div>
    );
  return (
    <div className="mt-6 overflow-x-auto rounded-xl border bg-white shadow-sm">
      <table className="min-w-full text-left text-sm">
        <thead className="bg-slate-50 text-xs uppercase text-slate-500">
          <tr>
            {[
              "Auction",
              "Status",
              "Boxes",
              "Wins",
              "Buyers",
              "Paid revenue",
              "COGS",
              "Fees",
              "Package costs",
              "Net profit",
              "",
            ].map((h) => (
              <th key={h} className="px-4 py-3">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sessions.map((s) => (
            <tr key={s._id} className="border-t">
              <td className="px-4 py-4">
                <strong>{s.name}</strong>
                <div className="text-xs text-slate-500">
                  {new Date(s.startedAt).toLocaleString()}
                </div>
              </td>
              <td className="px-4">{s.status}</td>
              <td className="px-4">
                {shipmentSummaryBySession[s._id]?.active ? (
                  <div className="space-y-1">
                    <div className="font-semibold text-slate-900">
                      {shipmentSummaryBySession[s._id]?.shipped}/
                      {shipmentSummaryBySession[s._id]?.active} shipped
                    </div>
                    <div className="text-xs text-slate-500">
                      {shipmentSummaryBySession[s._id]?.delivered
                        ? `${shipmentSummaryBySession[s._id]?.delivered} delivered`
                        : "None delivered yet"}
                    </div>
                  </div>
                ) : (
                  <span className="text-slate-500">No boxes</span>
                )}
              </td>
              <td className="px-4">{s.totals?.totalWins || 0}</td>
              <td className="px-4">{s.totals?.uniqueBuyers || 0}</td>
              <td className="px-4">
                {formatAuctionMoney(s.totals?.grossRevenueCents)}
              </td>
              <td className="px-4">
                {formatAuctionMoney(s.totals?.cogsCents)}
              </td>
              <td className="px-4">
                {formatAuctionMoney(s.totals?.platformFeesCents)}
              </td>
              <td className="px-4">
                {formatAuctionMoney(
                  (s.totals?.packagingCostCents || 0) +
                    (s.totals?.shippingCostCents || 0),
                )}
              </td>
              <td className="px-4 font-bold">
                {formatAuctionMoney(s.totals?.netProfitCents)}
              </td>
              <td className="px-4">
                <button
                  className="font-semibold text-blue-600"
                  onClick={() => open(s._id)}
                >
                  Open →
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Buyers({
  buyers,
  sessions,
  inventory,
  refresh,
  notify,
  addPrevious,
}: {
  buyers: AuctionBuyer[];
  sessions: AuctionSession[];
  inventory: IInventory[];
  refresh: () => Promise<void>;
  notify: any;
  addPrevious: (sessionId: string, buyer: string) => void;
}) {
  const [q, setQ] = useState(""),
    [username, setUsername] = useState(""),
    [display, setDisplay] = useState(""),
    [selected, setSelected] = useState<AuctionBuyerDetail | null>(null),
    [previousSession, setPreviousSession] = useState("");
  const add = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await liveAuctionsApi.createBuyer({ username, displayName: display });
      setUsername("");
      setDisplay("");
      await refresh();
      notify("Buyer saved.", { type: "success" });
    } catch (err) {
      notify(message(err), { type: "error" });
    }
  };
  const filtered = buyers.filter((b) =>
    `${b.username} ${b.displayName || ""}`
      .toLowerCase()
      .includes(q.toLowerCase()),
  );
  const sessionLookup = useMemo(
    () => new Map(sessions.map((session) => [session._id, session])),
    [sessions],
  );
  const inventoryMap = useMemo(
    () => new Map(inventory.map((item) => [item._id, item])),
    [inventory],
  );
  const selectedGroups = useMemo(() => {
    if (!selected) return [];
    const winsBySession = new Map<string, AuctionWin[]>();
    selected.wins.forEach((win) => {
      winsBySession.set(win.auctionSessionId, [
        ...(winsBySession.get(win.auctionSessionId) || []),
        win,
      ]);
    });
    return Array.from(winsBySession.entries())
      .map(([sessionId, wins]) => {
        const packages = selected.packages.filter(
          (pkg) => pkg.auctionSessionId === sessionId,
        );
        return {
          sessionId,
          session: sessionLookup.get(sessionId),
          wins: wins.map((win) => applyCurrentInventoryCost(win, inventoryMap)),
          packages,
          totals: summarizeBuyerGroup(wins, packages, inventoryMap),
        };
      })
      .sort((a, b) => {
        const aDate = a.session?.startedAt || a.wins[0]?.saleDate || "";
        const bDate = b.session?.startedAt || b.wins[0]?.saleDate || "";
        return bDate.localeCompare(aDate);
      });
  }, [selected, sessionLookup, inventoryMap]);
  const selectedSummary = useMemo(
    () =>
      selectedGroups.reduce(
        (sum, group) => ({
          totalUnits: sum.totalUnits + group.totals.totalUnits,
          grossRevenueCents:
            sum.grossRevenueCents + group.totals.grossRevenueCents,
          cogsCents: sum.cogsCents + group.totals.cogsCents,
          netProfitCents: sum.netProfitCents + group.totals.netProfitCents,
        }),
        {
          totalUnits: 0,
          grossRevenueCents: 0,
          cogsCents: 0,
          netProfitCents: 0,
        },
      ),
    [selectedGroups],
  );
  return (
    <div className="mt-6 grid gap-6 lg:grid-cols-[300px_1fr]">
      <form
        onSubmit={add}
        className="h-fit rounded-xl border bg-white p-5 shadow-sm"
      >
        <h2 className="font-bold">Add buyer</h2>
        <input
          required
          placeholder="@username"
          className={`${input} mt-4`}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
        <input
          placeholder="Display name (optional)"
          className={`${input} mt-3`}
          value={display}
          onChange={(e) => setDisplay(e.target.value)}
        />
        <button className={`${primary} mt-3 w-full`}>Save buyer</button>
      </form>
      <div>
        <input
          type="search"
          placeholder="Search buyers"
          className={input}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div className="mt-3 rounded-xl border bg-white">
          {filtered.map((b) => (
            <button
              key={b._id}
              className="flex w-full justify-between border-b p-4 text-left hover:bg-slate-50"
              onClick={async () =>
                setSelected(await liveAuctionsApi.buyer(b._id))
              }
            >
              <strong>{b.username}</strong>
              <span className="text-slate-500">
                {b.totalItemsPurchased || 0} items · Profit{" "}
                {formatAuctionMoney(b.totals?.netProfitCents)}
              </span>
            </button>
          ))}
        </div>
        {selected && (
          <div className="mt-5 rounded-xl border bg-white p-5">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <h2 className="text-xl font-bold">
                {selected.buyer.username} history
              </h2>
              <div className="flex gap-2">
                <select
                  aria-label="Auction session for previous purchase"
                  className={input}
                  value={previousSession}
                  onChange={(e) => setPreviousSession(e.target.value)}
                >
                  <option value="">Choose historical session</option>
                  {sessions.map((s) => (
                    <option key={s._id} value={s._id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                <button
                  disabled={!previousSession}
                  className={primary}
                  onClick={() =>
                    addPrevious(previousSession, selected.buyer.username)
                  }
                >
                  Add previous purchase
                </button>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
              <Metric
                label="Spend"
                value={formatAuctionMoney(selectedSummary.grossRevenueCents)}
              />
              <Metric label="Units" value={selectedSummary.totalUnits} />
              <Metric
                label="COGS"
                value={formatAuctionMoney(selectedSummary.cogsCents)}
              />
              <Metric
                label="Profit"
                value={formatAuctionMoney(selectedSummary.netProfitCents)}
              />
            </div>
            <div className="mt-5 space-y-4">
              {selectedGroups.map((group) => (
                <section
                  key={group.sessionId}
                  className="rounded-xl border border-slate-200 p-4"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="font-bold">
                        {group.session?.name || "Auction session"}
                      </h3>
                      <p className="text-sm text-slate-500">
                        {group.totals.totalUnits} units · Revenue{" "}
                        {formatAuctionMoney(group.totals.grossRevenueCents)} ·
                        Profit {formatAuctionMoney(group.totals.netProfitCents)}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm md:grid-cols-4">
                      <span>
                        Buyer paid{" "}
                        {formatAuctionMoney(
                          group.totals.buyerPaidShippingCents,
                        )}
                      </span>
                      <span>
                        Seller covers{" "}
                        {formatAuctionMoney(
                          group.totals.sellerCoveredShippingCents,
                        )}
                      </span>
                      <span>
                        Fees{" "}
                        {formatAuctionMoney(group.totals.platformFeesCents)}
                      </span>
                      <span>
                        COGS {formatAuctionMoney(group.totals.cogsCents)}
                      </span>
                    </div>
                  </div>
                  <div className="mt-3 space-y-2">
                    {group.wins.map((win) => (
                      <div
                        key={win._id}
                        className="flex justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm"
                      >
                        <span>
                          {new Date(win.saleDate).toLocaleDateString()} —{" "}
                          {win.inventoryItemNameSnapshot} × {win.quantity}
                        </span>
                        <span>
                          {formatAuctionMoney(
                            win.winningPriceCents * win.quantity,
                          )}{" "}
                          · {win.status}
                        </span>
                      </div>
                    ))}
                  </div>
                  {!!group.packages.length && (
                    <div className="mt-3 space-y-2">
                      {group.packages.map((pkg, index) => (
                        <div
                          key={pkg._id}
                          className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
                        >
                          <strong>Box {index + 1}</strong> · Actual ship{" "}
                          {formatAuctionMoney(pkg.actualShippingCostCents)} ·
                          Buyer paid{" "}
                          {formatAuctionMoney(
                            pkg.customerPaidShippingCents +
                              pkg.platformShippingCreditCents,
                          )}{" "}
                          · Seller covers{" "}
                          {formatAuctionMoney(packageSellerCovered(pkg))}
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Workspace({
  detail,
  inventory,
  buyers,
  tab,
  setTab,
  refresh,
  back,
  notify,
}: {
  detail: SessionDetail;
  inventory: IInventory[];
  buyers: AuctionBuyer[];
  tab: "capture" | "reconcile" | "presets";
  setTab: (v: "capture" | "reconcile" | "presets") => void;
  refresh: () => Promise<void>;
  back: () => void;
  notify: any;
}) {
  const inventoryMap = useMemo(
    () => new Map(inventory.map((item) => [item._id, item])),
    [inventory],
  );
  const t = useMemo(
    () => summarizeBuyerGroup(detail.wins, detail.packages, inventoryMap),
    [detail.wins, detail.packages, inventoryMap],
  );
  const totalWinUnits = detail.wins.reduce(
    (sum, win) => sum + Number(win.quantity || 0),
    0,
  );
  const [editingSession, setEditingSession] = useState(false);
  const [sessionName, setSessionName] = useState(detail.session.name);
  const [sessionDate, setSessionDate] = useState(
    localDateOnly(new Date(detail.session.startedAt)),
  );
  const [savingSession, setSavingSession] = useState(false);
  useEffect(() => {
    setSessionName(detail.session.name);
    setSessionDate(localDateOnly(new Date(detail.session.startedAt)));
  }, [detail.session.name, detail.session.startedAt]);
  const activePackages = detail.packages.filter(
    (pkg) => pkg.status !== "CANCELLED",
  );
  const unshippedPackages = activePackages.filter(
    (pkg) => pkg.status !== "SHIPPED" && pkg.status !== "DELIVERED",
  );
  const markAllOrdersShipped = async () => {
    if (!unshippedPackages.length) {
      notify("There are no orders left to mark shipped.", { type: "error" });
      return;
    }
    const confirmed = window.confirm(
      `Mark ${unshippedPackages.length} order${unshippedPackages.length === 1 ? "" : "s"} as shipped for this auction?`,
    );
    if (!confirmed) return;
    try {
      await Promise.all(
        unshippedPackages.map((pkg) =>
          liveAuctionsApi.updatePackage(pkg._id, { status: "SHIPPED" }),
        ),
      );
      await refresh();
      notify("All orders marked shipped.", { type: "success" });
    } catch (err) {
      notify(message(err), { type: "error" });
    }
  };
  const saveSessionMeta = async () => {
    try {
      setSavingSession(true);
      await liveAuctionsApi.updateSession(detail.session._id, {
        name: sessionName,
        startedAt: localDateOnlyToIso(sessionDate),
      });
      setEditingSession(false);
      await refresh();
      notify("Auction details updated.", { type: "success" });
    } catch (err) {
      notify(message(err), { type: "error" });
    } finally {
      setSavingSession(false);
    }
  };
  return (
    <main className="min-h-[calc(100vh-56px)] bg-slate-50 px-4 py-7 md:px-8">
      <div className="mx-auto max-w-7xl">
        <button className="font-semibold text-blue-600" onClick={back}>
          ← All auctions
        </button>
        <div className="mt-3 flex items-end justify-between">
          <div>
            {editingSession ? (
              <div className="grid gap-3 md:grid-cols-[minmax(320px,420px)_220px_auto] md:items-end">
                <label className="text-sm font-semibold">
                  Auction name
                  <input
                    className={`${input} mt-1`}
                    value={sessionName}
                    onChange={(event) => setSessionName(event.target.value)}
                  />
                </label>
                <label className="text-sm font-semibold">
                  Original date
                  <input
                    type="date"
                    className={`${input} mt-1`}
                    value={sessionDate}
                    onChange={(event) => setSessionDate(event.target.value)}
                  />
                </label>
                <div className="flex gap-2">
                  <button
                    className={primary}
                    disabled={savingSession}
                    onClick={() => void saveSessionMeta()}
                  >
                    {savingSession ? "Saving..." : "Save details"}
                  </button>
                  <button
                    className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700"
                    onClick={() => {
                      setEditingSession(false);
                      setSessionName(detail.session.name);
                      setSessionDate(
                        localDateOnly(new Date(detail.session.startedAt)),
                      );
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <h1 className="text-3xl font-bold">{detail.session.name}</h1>
                <p className="text-sm text-slate-500">
                  {new Date(detail.session.startedAt).toLocaleString()} · TikTok
                  fee {(detail.session.platformFeeBasisPoints / 100).toFixed(2)}
                  %
                </p>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <button
              className="rounded-lg border border-emerald-300 px-4 py-2.5 text-sm font-semibold text-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!unshippedPackages.length}
              onClick={() => void markAllOrdersShipped()}
            >
              Mark all orders shipped
            </button>
            <button
              className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700"
              onClick={() => setEditingSession((value) => !value)}
            >
              {editingSession ? "Close editor" : "Edit auction"}
            </button>
            <select
              aria-label="Session status"
              className={input}
              style={{ width: 190 }}
              value={detail.session.status}
              onChange={async (e) => {
                await liveAuctionsApi.updateSession(detail.session._id, {
                  status: e.target.value as any,
                });
                await refresh();
              }}
            >
              <option>ACTIVE</option>
              <option>RECONCILING</option>
              <option>CLOSED</option>
            </select>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-5 xl:grid-cols-10">
          <Metric label="Wins" value={totalWinUnits} />
          <Metric label="Buyers" value={t.uniqueBuyers} />
          <Metric
            label="Paid"
            value={formatAuctionMoney(t.grossRevenueCents)}
          />
          <Metric
            label="Pending"
            value={formatAuctionMoney(t.pendingAmountCents)}
          />
          <Metric label="Item cost" value={formatAuctionMoney(t.cogsCents)} />
          <Metric
            label="Fees"
            value={formatAuctionMoney(t.platformFeesCents)}
          />
          <Metric
            label="Packaging"
            value={formatAuctionMoney(t.packagingCostCents)}
          />
          <Metric
            label="Shipping"
            value={formatAuctionMoney(t.shippingCostCents)}
          />
          <Metric
            label="Net profit"
            value={formatAuctionMoney(t.netProfitCents)}
          />
          <Metric label="Margin %" value={`${t.marginPercent.toFixed(1)}%`} />
        </div>
        <div className="mt-6 flex gap-2 border-b">
          <button
            className={`px-4 py-3 font-semibold ${tab === "capture" ? "border-b-2 border-blue-600 text-blue-700" : "text-slate-500"}`}
            onClick={() => setTab("capture")}
          >
            Add wins
          </button>
          <button
            className={`px-4 py-3 font-semibold ${tab === "reconcile" ? "border-b-2 border-blue-600 text-blue-700" : "text-slate-500"}`}
            onClick={() => setTab("reconcile")}
          >
            Reconciliation
          </button>
          <button
            className={`px-4 py-3 font-semibold ${tab === "presets" ? "border-b-2 border-blue-600 text-blue-700" : "text-slate-500"}`}
            onClick={() => setTab("presets")}
          >
            Saved boxes
          </button>
        </div>
        {tab === "capture" ? (
          <Capture
            detail={detail}
            inventory={inventory}
            buyers={buyers}
            refresh={refresh}
            notify={notify}
          />
        ) : tab === "presets" ? (
          <BoxPresetManager notify={notify} />
        ) : (
          <Reconcile
            detail={detail}
            inventory={inventory}
            refresh={refresh}
            notify={notify}
          />
        )}
      </div>
    </main>
  );
}

export function Capture({
  detail,
  inventory,
  buyers,
  refresh,
  notify,
}: {
  detail: SessionDetail;
  inventory: IInventory[];
  buyers: AuctionBuyer[];
  refresh: () => Promise<void>;
  notify: any;
}) {
  const initialHistorical = inferHistoricalMode(detail.session);
  const [username, setUsername] = useState(
      () => new URLSearchParams(window.location.search).get("buyer") || "",
    ),
    [itemText, setItemText] = useState(""),
    [price, setPrice] = useState(""),
    [quantity, setQuantity] = useState("1"),
    [status, setStatus] = useState<WinStatus>("PAID"),
    [date, setDate] = useState(
      localDateOnly(new Date(detail.session.startedAt)),
    ),
    [historical, setHistorical] = useState(initialHistorical),
    [orderId, setOrderId] = useState(""),
    [notes, setNotes] = useState(""),
    [editingId, setEditingId] = useState<string | null>(null),
    [saving, setSaving] = useState(false),
    [selectedWinIds, setSelectedWinIds] = useState<string[]>([]),
    [packageDraft, setPackageDraft] = useState<PackageDraft>(emptyPackageDraft),
    [boxPresets, setBoxPresets] = useState<AuctionBoxPreset[]>([]),
    [selectedBoxPresetId, setSelectedBoxPresetId] = useState(""),
    [packageError, setPackageError] = useState(""),
    [reconciledBuyer, setReconciledBuyer] = useState("");
  const userRef = useRef<HTMLInputElement>(null),
    itemRef = useRef<HTMLInputElement>(null),
    priceRef = useRef<HTMLInputElement>(null);
  const savingRef = useRef(false);
  const selectionKeyRef = useRef("");
  useEffect(() => {
    if (isPastLocalDay(date)) {
      setHistorical(true);
    }
  }, [date]);
  const item = useMemo(() => {
    const q = itemText.trim().toLowerCase();
    return (
      inventory.find(
        (i) =>
          i._id === itemText ||
          i.itemName.toLowerCase() === q ||
          i.sku?.toLowerCase() === q,
      ) ||
      inventory.find((i) =>
        `${i.itemName} ${i.sku || ""} ${(i as any).brand || ""}`
          .toLowerCase()
          .includes(q),
      )
    );
  }, [itemText, inventory]);
  const normalizedUsername = normalizeUsername(username);
  const packagedWinIds = useMemo(
    () => new Set(detail.packages.flatMap((pkg) => pkg.auctionWinIds)),
    [detail.packages],
  );
  const buyerWins = useMemo(
    () =>
      detail.wins.filter(
        (win) => win.normalizedUsername === normalizedUsername,
      ),
    [detail.wins, normalizedUsername],
  );
  const unreconciledWins = useMemo(
    () => buyerWins.filter((win) => !packagedWinIds.has(win._id)),
    [buyerWins, packagedWinIds],
  );
  const unreconciledUnits = useMemo(
    () => unreconciledWins.reduce((sum, win) => sum + win.quantity, 0),
    [unreconciledWins],
  );
  const unreconciledKey = unreconciledWins.map((win) => win._id).join("|");
  useEffect(() => {
    const nextKey = `${normalizedUsername}:${unreconciledKey}`;
    if (selectionKeyRef.current !== nextKey) {
      selectionKeyRef.current = nextKey;
      setSelectedWinIds(unreconciledWins.map((win) => win._id));
    }
  }, [normalizedUsername, unreconciledKey, unreconciledWins]);
  useEffect(() => {
    liveAuctionsApi
      .boxPresets()
      .then(setBoxPresets)
      .catch(() => setBoxPresets([]));
  }, []);
  const hasWinDraft = Boolean(itemText.trim() || price || editingId);
  const hasPackageDraft = Object.values(packageDraft).some((value) =>
    value.trim(),
  );
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (!hasWinDraft && !hasPackageDraft) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [hasWinDraft, hasPackageDraft]);
  const recentBuyers = Array.from(
      new Map(
        detail.wins.map((w) => [w.normalizedUsername, w.usernameSnapshot]),
      ).values(),
    ).slice(0, 8),
    recentItems = Array.from(
      new Map(
        detail.wins.map((w) => [
          w.inventoryItemId,
          w.inventoryItemNameSnapshot,
        ]),
      ).values(),
    ).slice(0, 8);
  const validateWinDraft = () => {
    if (!username.trim() || !item || !price)
      return (
        notify("Enter username, select inventory, and enter price.", {
          type: "error",
        }),
        false
      );
    if (!Number.isInteger(Number(price)))
      return (
        notify("Winning price must be a whole dollar amount.", {
          type: "error",
        }),
        false
      );
    if (!Number.isInteger(Number(quantity)) || Number(quantity) < 1)
      return (
        notify("Quantity must be a positive whole number.", {
          type: "error",
        }),
        false
      );
    return true;
  };
  const saveCurrentWin = async () => {
    if (!validateWinDraft()) return null;
    const common: any = {
      inventoryItemId: item!._id,
      winningPrice: Number(price),
      quantity: Number(quantity),
      status,
      saleDate: localDateOnlyToIso(date),
      tiktokOrderId: orderId,
      notes,
    };
    if (editingId) {
      const buyer = await liveAuctionsApi.createBuyer({ username });
      return liveAuctionsApi.updateWin(editingId, {
        ...common,
        auctionBuyerId: buyer._id,
      });
    }
    return liveAuctionsApi.createWin({
      ...common,
      auctionSessionId: detail.session._id,
      username,
      historicalImport: historical,
      source: historical ? "HISTORICAL_IMPORT" : "LIVE_CAPTURE",
      applyInventoryAdjustment: false,
      clientSubmissionId: submissionId(),
    });
  };
  const resetWinForm = () => {
    setEditingId(null);
    setItemText("");
    setPrice("");
    setQuantity("1");
    setStatus("PAID");
    setOrderId("");
    setNotes("");
  };
  const save = async () => {
    if (savingRef.current) return;
    try {
      savingRef.current = true;
      setSaving(true);
      const wasEditing = Boolean(editingId);
      const saved = await saveCurrentWin();
      if (!saved) return;
      notify(
        wasEditing
          ? "Auction win updated."
          : `${username} — ${item!.itemName} saved.`,
        { type: "success" },
      );
      resetWinForm();
      await refresh();
      userRef.current?.focus();
      userRef.current?.select();
    } catch (err) {
      notify(message(err), { type: "error" });
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };
  const saveWinsAndReconcile = async () => {
    if (savingRef.current || editingId) return;
    if (!selectedWinIds.length && !hasWinDraft) {
      setPackageError(
        "Select at least one unreconciled win or enter a new win.",
      );
      return;
    }
    let createdWin: AuctionWin | null = null;
    try {
      savingRef.current = true;
      setSaving(true);
      setPackageError("");
      if (hasWinDraft) {
        createdWin = await saveCurrentWin();
        if (!createdWin) return;
      }
      const finalWinIds = Array.from(
        new Set([
          ...selectedWinIds.filter((id) =>
            unreconciledWins.some((win) => win._id === id),
          ),
          ...(createdWin ? [createdWin._id] : []),
        ]),
      );
      const buyerId =
        createdWin?.auctionBuyerId ||
        unreconciledWins.find((win) => finalWinIds.includes(win._id))
          ?.auctionBuyerId;
      if (!buyerId || !finalWinIds.length)
        throw new Error(
          "Select at least one unreconciled win for this package.",
        );
      await persistAuctionPackage(
        detail.session._id,
        buyerId,
        finalWinIds,
        packageDraft,
      );
      const completedBuyer = username;
      resetWinForm();
      await refresh();
      setUsername("");
      setPackageDraft(emptyPackageDraft());
      setSelectedBoxPresetId("");
      setSelectedWinIds([]);
      setPackageError("");
      setReconciledBuyer("");
      window.setTimeout(() => userRef.current?.focus(), 0);
      notify(`${completedBuyer}'s wins and package were saved.`, {
        type: "success",
      });
    } catch (err) {
      if (createdWin) {
        try {
          await liveAuctionsApi.deleteWin(createdWin._id);
          await refresh();
        } catch {
          notify(
            "Package failed and the newly saved win could not be rolled back. Review it in Reconciliation.",
            { type: "error" },
          );
        }
      }
      setPackageError(message(err));
      notify(message(err), { type: "error" });
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };
  const processNextBuyer = () => {
    resetWinForm();
    setUsername("");
    setPackageDraft(emptyPackageDraft());
    setSelectedBoxPresetId("");
    setSelectedWinIds([]);
    setPackageError("");
    setReconciledBuyer("");
    window.setTimeout(() => userRef.current?.focus(), 0);
  };
  const setPackageField = (field: keyof PackageDraft, value: string) =>
    setPackageDraft((current) => ({ ...current, [field]: value }));
  const key =
    (next: React.RefObject<HTMLInputElement>, final = false) =>
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        final ? void save() : next.current?.focus();
      }
    };
  const updateStatus = async (w: AuctionWin, next: WinStatus) => {
    try {
      await liveAuctionsApi.updateWin(w._id, { status: next });
      await refresh();
    } catch (err) {
      notify(message(err), { type: "error" });
    }
  };
  return (
    <>
      <div className="mt-6 grid gap-6 xl:grid-cols-[1.05fr_.95fr]">
        <section className="rounded-2xl border border-blue-200 bg-white p-5 shadow-sm">
          <div className="flex justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold">
                {editingId ? "Edit auction win" : "Record a winning bid"}
              </h2>
              <p className="text-sm text-slate-500">
                Buyer stays selected. Pick inventory, enter whole-dollar winning
                price, save.
              </p>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={historical}
                onChange={(e) => setHistorical(e.target.checked)}
              />
              Backfill mode
            </label>
          </div>
          <div
            className={`mt-4 rounded-xl border px-4 py-3 text-sm ${historical ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-amber-200 bg-amber-50 text-amber-900"}`}
          >
            {historical
              ? "Backfill mode keeps current inventory untouched. The win stays linked to the inventory item, so current cost and item details come from inventory."
              : "Live mode can affect current inventory when a paid win is recorded. Use backfill mode for wins from earlier dates."}
          </div>
          <div className="mt-5 grid gap-4 md:grid-cols-[1fr_1.4fr_.7fr_auto]">
            <label className="text-xs font-bold uppercase text-slate-500">
              TikTok buyer
              <input
                autoFocus
                ref={userRef}
                list="auction-buyers"
                className={`${input} mt-1`}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyDown={key(itemRef)}
              />
            </label>
            <datalist id="auction-buyers">
              {buyers.map((b) => (
                <option key={b._id} value={b.username} />
              ))}
            </datalist>
            <label className="text-xs font-bold uppercase text-slate-500">
              Inventory item
              <input
                ref={itemRef}
                list="auction-items"
                placeholder="Search item name, SKU, or brand"
                className={`${input} mt-1`}
                value={itemText}
                onChange={(e) => setItemText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && item) {
                    e.preventDefault();
                    setItemText(item.itemName);
                    priceRef.current?.focus();
                  }
                }}
              />
            </label>
            <datalist id="auction-items">
              {inventory.map((i) => (
                <option key={i._id} value={i.itemName}>
                  {i.sku}
                </option>
              ))}
            </datalist>
            <label className="text-xs font-bold uppercase text-slate-500">
              Winning price
              <input
                ref={priceRef}
                type="number"
                min="0"
                step="1"
                inputMode="numeric"
                className={`${input} mt-1`}
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                onKeyDown={key(userRef, true)}
              />
            </label>
            <button
              disabled={saving}
              className={`${primary} self-end`}
              onClick={() => void save()}
            >
              {saving
                ? "Saving..."
                : editingId
                  ? "Update win"
                  : "Save Wins Only"}
            </button>
          </div>
          {item && (
            <div className="mt-3 rounded-lg bg-slate-50 p-3 text-sm">
              <p>
                <strong>{item.itemName}</strong>
                {item.sku ? ` · SKU ${item.sku}` : ""}
                {item.brand ? ` · ${item.brand}` : ""}
              </p>
              <p className="mt-1 text-slate-600">
                Current inventory cost{" "}
                {formatAuctionMoney(Math.round((item.costPerUnit || 0) * 100))}.
                Default selling price is ignored for auction wins.
              </p>
            </div>
          )}
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <label className="text-xs font-semibold">
              Quantity
              <input
                type="number"
                min="1"
                className={`${input} mt-1`}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </label>
            <label className="text-xs font-semibold">
              Payment
              <select
                className={`${input} mt-1`}
                value={status}
                onChange={(e) => setStatus(e.target.value as WinStatus)}
              >
                <option>PENDING</option>
                <option>PAID</option>
                <option>PAYMENT_FAILED</option>
                <option>CANCELLED</option>
                <option>REFUNDED</option>
              </select>
            </label>
            <label className="text-xs font-semibold">
              Original sale date
              <input
                type="date"
                className={`${input} mt-1`}
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </label>
            <label className="text-xs font-semibold">
              TikTok order ID
              <input
                className={`${input} mt-1`}
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
              />
            </label>
            <label className="text-xs font-semibold">
              Notes
              <input
                className={`${input} mt-1`}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </label>
          </div>
          <Shortcut
            label="Recent buyers"
            values={recentBuyers}
            choose={(v) => {
              setUsername(v);
              itemRef.current?.focus();
            }}
          />
          <Shortcut
            label="Recent inventory"
            values={recentItems}
            choose={(v) => {
              setItemText(v);
              priceRef.current?.focus();
            }}
          />
        </section>
        <section className="rounded-2xl border border-blue-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-bold">Package &amp; Reconcile</h2>
              <p className="text-sm text-slate-500">
                Package only the checked, currently unreconciled wins using the
                same records as the Reconciliation screen.
              </p>
            </div>
            {reconciledBuyer && (
              <button className={primary} onClick={processNextBuyer}>
                Process Next Buyer
              </button>
            )}
          </div>
          {normalizedUsername ? (
            <>
              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                <Metric
                  label="Buyer"
                  value={username}
                  valueClassName="break-all text-[1.8rem] leading-tight"
                />
                <Metric label="Unreconciled wins" value={unreconciledUnits} />
                <Metric
                  label="Selected sales"
                  value={formatAuctionMoney(
                    unreconciledWins
                      .filter((win) => selectedWinIds.includes(win._id))
                      .reduce(
                        (sum, win) =>
                          sum + win.winningPriceCents * win.quantity,
                        0,
                      ) +
                      (hasWinDraft && Number.isFinite(Number(price))
                        ? Number(price) * Number(quantity || 0) * 100
                        : 0),
                  )}
                />
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50"
                  disabled={!unreconciledWins.length}
                  onClick={() =>
                    setSelectedWinIds(unreconciledWins.map((win) => win._id))
                  }
                >
                  Select all unreconciled wins
                </button>
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50"
                  disabled={!selectedWinIds.length}
                  onClick={() => setSelectedWinIds([])}
                >
                  Clear selection
                </button>
              </div>
              <div className="mt-4 grid gap-2 md:grid-cols-2">
                {unreconciledWins.map((win) => (
                  <label
                    key={win._id}
                    className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3"
                  >
                    <span className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        aria-label={`Include ${win.inventoryItemNameSnapshot}`}
                        checked={selectedWinIds.includes(win._id)}
                        onChange={(event) =>
                          setSelectedWinIds((current) =>
                            event.target.checked
                              ? Array.from(new Set([...current, win._id]))
                              : current.filter((id) => id !== win._id),
                          )
                        }
                      />
                      {win.inventoryItemNameSnapshot} × {win.quantity}
                    </span>
                    <strong>
                      {formatAuctionMoney(win.winningPriceCents * win.quantity)}
                    </strong>
                  </label>
                ))}
              </div>
              {!unreconciledWins.length && !hasWinDraft && (
                <p className="mt-4 rounded-lg bg-slate-50 p-3 text-sm text-slate-500">
                  This buyer has no unreconciled wins in this auction. Enter a
                  new win above or choose another buyer.
                </p>
              )}
              {hasWinDraft && !editingId && (
                <p className="mt-3 rounded-lg bg-blue-50 p-3 text-sm text-blue-800">
                  The win currently entered above will be saved first and
                  included in this package.
                </p>
              )}
              <div className="mt-5 grid gap-3 md:grid-cols-4">
                <label className="text-sm font-semibold">
                  Saved box preset
                  <select
                    className={`${input} mt-1`}
                    value={selectedBoxPresetId}
                    onChange={(event) => {
                      const preset = boxPresets.find(
                        (row) => row._id === event.target.value,
                      );
                      setSelectedBoxPresetId(event.target.value);
                      if (preset)
                        setPackageDraft((current) => ({
                          ...current,
                          boxSizeLabel: preset.label,
                          packaging: (preset.packagingCostCents / 100).toFixed(
                            2,
                          ),
                        }));
                    }}
                  >
                    <option value="">Choose saved box size</option>
                    {boxPresets.map((preset) => (
                      <option key={preset._id} value={preset._id}>
                        {preset.label} ·{" "}
                        {formatAuctionMoney(preset.packagingCostCents)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-sm font-semibold">
                  Box size
                  <input
                    className={`${input} mt-1`}
                    placeholder="e.g. 4x4x4"
                    value={packageDraft.boxSizeLabel}
                    onChange={(event) => {
                      setSelectedBoxPresetId("");
                      setPackageField("boxSizeLabel", event.target.value);
                    }}
                  />
                </label>
                <label className="text-sm font-semibold">
                  Packaging cost
                  <input
                    aria-label="Package packaging cost"
                    type="number"
                    min="0"
                    step=".01"
                    className={`${input} mt-1`}
                    value={packageDraft.packaging}
                    onChange={(event) =>
                      setPackageField("packaging", event.target.value)
                    }
                  />
                </label>
                <label className="text-sm font-semibold">
                  Actual shipping cost
                  <input
                    aria-label="Package actual shipping cost"
                    type="number"
                    min="0"
                    step=".01"
                    className={`${input} mt-1`}
                    value={packageDraft.shipping}
                    onChange={(event) =>
                      setPackageField("shipping", event.target.value)
                    }
                  />
                </label>
                <label className="text-sm font-semibold">
                  Buyer paid shipping
                  <input
                    type="number"
                    min="0"
                    step=".01"
                    className={`${input} mt-1`}
                    value={packageDraft.income}
                    onChange={(event) =>
                      setPackageField("income", event.target.value)
                    }
                  />
                </label>
                <label className="text-sm font-semibold">
                  Shipping credit
                  <input
                    type="number"
                    min="0"
                    step=".01"
                    className={`${input} mt-1`}
                    value={packageDraft.credit}
                    onChange={(event) =>
                      setPackageField("credit", event.target.value)
                    }
                  />
                </label>
                <label className="text-sm font-semibold">
                  TikTok order ID
                  <input
                    className={`${input} mt-1`}
                    value={packageDraft.order}
                    onChange={(event) =>
                      setPackageField("order", event.target.value)
                    }
                  />
                </label>
                <label className="text-sm font-semibold">
                  Tracking number
                  <input
                    className={`${input} mt-1`}
                    value={packageDraft.tracking}
                    onChange={(event) =>
                      setPackageField("tracking", event.target.value)
                    }
                  />
                </label>
              </div>
              {packageError && (
                <p
                  role="alert"
                  className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700"
                >
                  {packageError}
                </p>
              )}
              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  type="button"
                  className={primary}
                  disabled={saving || Boolean(editingId)}
                  onClick={() => void saveWinsAndReconcile()}
                >
                  {saving ? "Saving..." : "Save Wins & Reconcile Package"}
                </button>
                {editingId && (
                  <span className="self-center text-sm text-slate-500">
                    Finish updating this win before creating a package.
                  </span>
                )}
              </div>
            </>
          ) : (
            <p className="mt-4 rounded-lg bg-slate-50 p-4 text-sm text-slate-500">
              Enter or select a TikTok buyer above to view their unreconciled
              wins.
            </p>
          )}
        </section>
      </div>
      <aside className="mt-6 rounded-2xl border bg-white p-5 shadow-sm">
        <h2 className="font-bold">Recent wins</h2>
        <div className="mt-3 max-h-[620px] divide-y overflow-auto">
          {detail.wins.slice(0, 20).map((w) => (
            <div key={w._id} className="py-3">
              <div className="flex justify-between">
                <span>
                  <strong>{w.usernameSnapshot}</strong>
                  <small className="block text-slate-500">
                    {w.inventoryItemNameSnapshot} × {w.quantity}
                  </small>
                </span>
                <strong>
                  {formatAuctionMoney(w.winningPriceCents * w.quantity)}
                </strong>
              </div>
              <div className="mt-2 flex gap-2">
                <select
                  aria-label={`Payment status for ${w.inventoryItemNameSnapshot}`}
                  className="rounded border px-2 py-1 text-xs"
                  value={w.status}
                  onChange={(e) =>
                    void updateStatus(w, e.target.value as WinStatus)
                  }
                >
                  <option>PENDING</option>
                  <option>PAID</option>
                  <option>PAYMENT_FAILED</option>
                  <option>CANCELLED</option>
                  <option>REFUNDED</option>
                </select>
                <button
                  className="text-xs font-bold text-red-600"
                  onClick={async () => {
                    await liveAuctionsApi.deleteWin(w._id);
                    await refresh();
                    notify("Win deleted.", { type: "success" });
                  }}
                >
                  Delete
                </button>
                <button
                  className="text-xs font-bold text-blue-600"
                  onClick={() => {
                    setEditingId(w._id);
                    setUsername(w.usernameSnapshot);
                    setItemText(w.inventoryItemNameSnapshot);
                    setPrice((w.winningPriceCents / 100).toFixed(2));
                    setQuantity(String(w.quantity));
                    setStatus(w.status);
                    setDate(localDateOnly(new Date(w.saleDate)));
                    setOrderId(w.tiktokOrderId || "");
                    setNotes(w.notes || "");
                    userRef.current?.focus();
                  }}
                >
                  Edit
                </button>
              </div>
            </div>
          ))}
        </div>
      </aside>
    </>
  );
}
function Shortcut({
  label,
  values,
  choose,
}: {
  label: string;
  values: string[];
  choose: (v: string) => void;
}) {
  return (
    <div className="mt-4">
      <p className="text-xs font-bold uppercase text-slate-500">{label}</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {values.map((v) => (
          <button
            key={v}
            onClick={() => choose(v)}
            className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm text-blue-700"
          >
            {v}
          </button>
        ))}
      </div>
    </div>
  );
}

function BoxPresetManager({ notify }: { notify: any }) {
  const [boxPresets, setBoxPresets] = useState<AuctionBoxPreset[]>([]);
  const [selectedBoxPresetId, setSelectedBoxPresetId] = useState("");
  const [label, setLabel] = useState("");
  const [packaging, setPackaging] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadBoxPresets = useCallback(async () => {
    try {
      setLoading(true);
      setBoxPresets(await liveAuctionsApi.boxPresets());
    } catch (err) {
      notify(message(err), { type: "error" });
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    void loadBoxPresets();
  }, [loadBoxPresets]);

  const resetForm = () => {
    setSelectedBoxPresetId("");
    setLabel("");
    setPackaging("");
  };

  const selectedPreset =
    boxPresets.find((preset) => preset._id === selectedBoxPresetId) || null;

  const canSave =
    label.trim().length > 0 &&
    packaging.trim() !== "" &&
    Number.isFinite(Number(packaging)) &&
    Number(packaging) >= 0;

  const savePreset = async () => {
    try {
      if (!canSave) {
        notify("Enter a saved box name and packaging price first.", {
          type: "error",
        });
        return;
      }
      setSaving(true);
      if (selectedBoxPresetId) {
        await liveAuctionsApi.updateBoxPreset(selectedBoxPresetId, {
          label: label.trim(),
          packagingCost: Number(packaging),
        });
        notify("Saved box updated.", { type: "success" });
      } else {
        const created = await liveAuctionsApi.createBoxPreset({
          label: label.trim(),
          packagingCost: Number(packaging),
        });
        setSelectedBoxPresetId(created._id);
        notify("Saved box created.", { type: "success" });
      }
      await loadBoxPresets();
    } catch (err) {
      notify(message(err), { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const editPreset = (preset: AuctionBoxPreset) => {
    setSelectedBoxPresetId(preset._id);
    setLabel(preset.label);
    setPackaging((preset.packagingCostCents / 100).toFixed(2));
  };

  const removePreset = async (id: string) => {
    try {
      setSaving(true);
      await liveAuctionsApi.deleteBoxPreset(id);
      if (selectedBoxPresetId === id) resetForm();
      await loadBoxPresets();
      notify("Saved box deleted.", { type: "success" });
    } catch (err) {
      notify(message(err), { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mt-6 rounded-2xl border border-blue-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold">Saved Box Presets</h2>
          <p className="text-sm text-slate-500">
            Create, edit, or delete saved package sizes and packaging costs.
            These same presets appear in the Saved box preset dropdown.
          </p>
        </div>
        {selectedPreset && (
          <button
            type="button"
            className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700"
            onClick={resetForm}
          >
            New preset
          </button>
        )}
      </div>

      <div className="mt-5 grid gap-6 xl:grid-cols-[420px_1fr]">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <h3 className="text-sm font-bold uppercase tracking-wide text-slate-600">
            {selectedPreset ? "Edit saved box" : "Create saved box"}
          </h3>
          <div className="mt-4 space-y-4">
            <label className="block text-sm font-semibold">
              Preset name
              <input
                className={`${input} mt-1`}
                placeholder="e.g. 4x4x4"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
              />
            </label>
            <label className="block text-sm font-semibold">
              Packaging cost
              <input
                className={`${input} mt-1`}
                type="number"
                min="0"
                step=".01"
                placeholder="0.00"
                value={packaging}
                onChange={(event) => setPackaging(event.target.value)}
              />
            </label>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                className={primary}
                disabled={saving}
                onClick={() => void savePreset()}
              >
                {saving
                  ? "Saving..."
                  : selectedPreset
                    ? "Save preset"
                    : "Create preset"}
              </button>
              <button
                type="button"
                className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700"
                onClick={resetForm}
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white">
          <div className="border-b border-slate-200 px-4 py-3">
            <h3 className="font-semibold">Existing saved boxes</h3>
          </div>
          {loading ? (
            <p className="p-4 text-sm text-slate-500">Loading saved boxes...</p>
          ) : boxPresets.length ? (
            <div className="divide-y">
              {boxPresets.map((preset) => (
                <div
                  key={preset._id}
                  className="flex flex-wrap items-center justify-between gap-3 px-4 py-4"
                >
                  <div>
                    <div className="font-semibold text-slate-900">
                      {preset.label}
                    </div>
                    <div className="text-sm text-slate-500">
                      Packaging cost{" "}
                      {formatAuctionMoney(preset.packagingCostCents)}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700"
                      onClick={() => editPreset(preset)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="rounded-lg border border-red-200 px-3 py-2 text-sm font-semibold text-red-600"
                      onClick={() => void removePreset(preset._id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="p-4 text-sm text-slate-500">
              No saved box presets yet. Create one here and it will appear in
              the Saved box preset dropdown.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

function Reconcile({
  detail,
  inventory,
  refresh,
  notify,
}: {
  detail: SessionDetail;
  inventory: IInventory[];
  refresh: () => Promise<void>;
  notify: any;
}) {
  const [buyerFilter, setBuyerFilter] = useState("");
  const inventoryMap = useMemo(
    () => new Map(inventory.map((item) => [item._id, item])),
    [inventory],
  );
  const groups = useMemo(
    () => groupWinsByBuyer(detail.wins, detail.packages, inventoryMap),
    [detail.wins, detail.packages, inventoryMap],
  );
  const filteredGroups = groups.filter((group) =>
    `${group.username} ${group.wins
      .map((win) => win.inventoryItemNameSnapshot)
      .join(" ")}`
      .toLowerCase()
      .includes(buyerFilter.toLowerCase()),
  );
  return (
    <div className="mt-6 space-y-5">
      <input
        type="search"
        placeholder="Search buyer or item in this live"
        className={input}
        value={buyerFilter}
        onChange={(event) => setBuyerFilter(event.target.value)}
      />
      {filteredGroups.map((group) => (
        <BuyerPackages
          key={group.buyerId}
          buyer={group}
          inventory={inventory}
          session={detail.session._id}
          refresh={refresh}
          notify={notify}
        />
      ))}
      {!filteredGroups.length && (
        <p className="rounded-xl border border-dashed p-8 text-center text-slate-500">
          {groups.length
            ? "No buyer matches this search."
            : "Capture wins before reconciliation."}
        </p>
      )}
    </div>
  );
}
function BuyerPackages({
  buyer,
  inventory,
  session,
  refresh,
  notify,
}: {
  buyer: BuyerGroup;
  inventory: IInventory[];
  session: string;
  refresh: () => Promise<void>;
  notify: any;
}) {
  const { buyerId, username, wins, packages } = buyer;
  const inventoryMap = useMemo(
    () => new Map(inventory.map((item) => [item._id, item])),
    [inventory],
  );
  const totals = useMemo(
    () => summarizeBuyerGroup(wins, packages, inventoryMap),
    [wins, packages, inventoryMap],
  );
  const [selected, setSelected] = useState<string[]>([]),
    [boxSizeLabel, setBoxSizeLabel] = useState(""),
    [selectedBoxPresetId, setSelectedBoxPresetId] = useState(""),
    [boxPresets, setBoxPresets] = useState<AuctionBoxPreset[]>([]),
    [packaging, setPackaging] = useState(""),
    [shipping, setShipping] = useState(""),
    [income, setIncome] = useState(""),
    [credit, setCredit] = useState(""),
    [order, setOrder] = useState(""),
    [tracking, setTracking] = useState(""),
    [boxError, setBoxError] = useState(""),
    [editingPackageId, setEditingPackageId] = useState<string | null>(null),
    [editingWinId, setEditingWinId] = useState<string | null>(null),
    [editingWinItem, setEditingWinItem] = useState(""),
    [editingWinItemId, setEditingWinItemId] = useState(""),
    [editingWinPrice, setEditingWinPrice] = useState(""),
    [editingWinQuantity, setEditingWinQuantity] = useState("1");
  const loadBoxPresets = useCallback(async () => {
    const rows = await liveAuctionsApi.boxPresets();
    setBoxPresets(rows);
  }, []);
  useEffect(() => {
    void loadBoxPresets();
  }, [loadBoxPresets]);
  const editingPackage = packages.find((pkg) => pkg._id === editingPackageId);
  const editingWin = wins.find((win) => win._id === editingWinId) || null;
  const assigned = new Set(
    packages
      .filter((pkg) => pkg._id !== editingPackageId)
      .flatMap((pkg) => pkg.auctionWinIds),
  );
  const availableWinIds = wins
    .filter((win) => !assigned.has(win._id))
    .map((win) => win._id);
  const canCreateBox = selected.length > 0;
  const matchingInventory = inventory.filter((item) =>
    `${item.itemName} ${item.sku || ""} ${item.brand || ""}`
      .toLowerCase()
      .includes(editingWinItem.trim().toLowerCase()),
  );
  const selectedInventoryItem =
    inventory.find((item) => item._id === editingWinItemId) ||
    inventory.find(
      (item) =>
        item.itemName.toLowerCase() === editingWinItem.trim().toLowerCase() ||
        item.sku?.toLowerCase() === editingWinItem.trim().toLowerCase(),
    ) ||
    matchingInventory[0];
  const resetBoxForm = () => {
    setSelected([]);
    setBoxSizeLabel("");
    setSelectedBoxPresetId("");
    setPackaging("");
    setShipping("");
    setIncome("");
    setCredit("");
    setOrder("");
    setTracking("");
    setEditingPackageId(null);
    setBoxError("");
  };
  const canSaveBoxPreset =
    boxSizeLabel.trim().length > 0 && !Number.isNaN(Number(packaging || 0));
  const resetWinEdit = () => {
    setEditingWinId(null);
    setEditingWinItem("");
    setEditingWinItemId("");
    setEditingWinPrice("");
    setEditingWinQuantity("1");
  };
  const saveBox = async () => {
    try {
      if (!selected.length) {
        setBoxError("Select at least one won item for this box.");
        return;
      }
      setBoxError("");
      await persistAuctionPackage(
        session,
        buyerId,
        selected,
        { boxSizeLabel, packaging, shipping, income, credit, order, tracking },
        editingPackageId,
      );
      resetBoxForm();
      await refresh();
      notify(editingPackageId ? "Box updated." : "Box created.", {
        type: "success",
      });
    } catch (err) {
      setBoxError(message(err));
      notify(message(err), { type: "error" });
    }
  };
  const saveWinEdit = async () => {
    if (!editingWin || !selectedInventoryItem) {
      notify("Choose the correct inventory item for this win.", {
        type: "error",
      });
      return;
    }
    if (!Number.isInteger(Number(editingWinPrice))) {
      notify("Winning price must be a whole dollar amount.", {
        type: "error",
      });
      return;
    }
    if (
      !Number.isInteger(Number(editingWinQuantity)) ||
      Number(editingWinQuantity) < 1
    ) {
      notify("Quantity must be a positive whole number.", {
        type: "error",
      });
      return;
    }
    try {
      await liveAuctionsApi.updateWin(editingWin._id, {
        inventoryItemId: selectedInventoryItem._id,
        winningPrice: Number(editingWinPrice),
        quantity: Number(editingWinQuantity),
      });
      resetWinEdit();
      await refresh();
      notify("Win updated.", { type: "success" });
    } catch (err) {
      notify(message(err), { type: "error" });
    }
  };
  const saveBoxPreset = async () => {
    try {
      if (!canSaveBoxPreset) {
        notify("Enter a box size and packaging price first.", {
          type: "error",
        });
        return;
      }
      if (selectedBoxPresetId) {
        await liveAuctionsApi.updateBoxPreset(selectedBoxPresetId, {
          label: boxSizeLabel.trim(),
          packagingCost: Number(packaging || 0),
        });
      } else {
        const created = await liveAuctionsApi.createBoxPreset({
          label: boxSizeLabel.trim(),
          packagingCost: Number(packaging || 0),
        });
        setSelectedBoxPresetId(created._id);
      }
      await loadBoxPresets();
      notify("Box preset saved.", { type: "success" });
    } catch (err) {
      notify(message(err), { type: "error" });
    }
  };
  const removeBoxPreset = async () => {
    try {
      if (!selectedBoxPresetId) return;
      await liveAuctionsApi.deleteBoxPreset(selectedBoxPresetId);
      setSelectedBoxPresetId("");
      await loadBoxPresets();
      notify("Box preset deleted.", { type: "success" });
    } catch (err) {
      notify(message(err), { type: "error" });
    }
  };
  const activePackages = packages.filter((pkg) => pkg.status !== "CANCELLED");
  const shippedPackages = activePackages.filter((pkg) =>
    ["SHIPPED", "DELIVERED"].includes(pkg.status),
  );
  const cancelBuyerOrder = async () => {
    const cancellableWins = wins.filter(
      (win) => win.status !== "CANCELLED" && win.status !== "REFUNDED",
    );
    const cancellablePackages = packages.filter(
      (pkg) => pkg.status !== "CANCELLED",
    );
    if (!cancellableWins.length && !cancellablePackages.length) {
      notify("This order is already cancelled or refunded.", {
        type: "error",
      });
      return;
    }
    const confirmed = window.confirm(
      `Cancel ${username}'s order? Paid wins will be marked refunded, unpaid wins will be cancelled, and attached boxes will be cancelled.`,
    );
    if (!confirmed) return;
    try {
      await Promise.all([
        ...cancellableWins.map((win) =>
          liveAuctionsApi.updateWin(win._id, {
            status: win.status === "PAID" ? "REFUNDED" : "CANCELLED",
          }),
        ),
        ...cancellablePackages.map((pkg) =>
          liveAuctionsApi.updatePackage(pkg._id, {
            status: "CANCELLED",
          }),
        ),
      ]);
      resetBoxForm();
      resetWinEdit();
      await refresh();
      notify("Order cancelled and refunded.", { type: "success" });
    } catch (err) {
      notify(message(err), { type: "error" });
    }
  };
  return (
    <section className="rounded-xl border bg-white p-5 shadow-sm">
      <div className="flex flex-wrap justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold">{username}</h2>
          <p className="text-sm text-slate-500">
            {totals.totalUnits} item wins ·{" "}
            {formatAuctionMoney(totals.netProfitCents)} profit
          </p>
          <p className="text-sm text-slate-500">
            Box status: {shippedPackages.length}/{activePackages.length} shipped
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            className="rounded-lg border border-red-300 px-3 py-2 text-sm font-bold text-red-700"
            onClick={() => void cancelBuyerOrder()}
          >
            Cancel order
          </button>
          <button
            className="rounded-lg border border-blue-300 px-3 py-2 text-sm font-bold text-blue-700"
            onClick={async () => {
              await Promise.all(
                wins
                  .filter((w) => w.status === "PENDING")
                  .map((w) =>
                    liveAuctionsApi.updateWin(w._id, { status: "PAID" }),
                  ),
              );
              await refresh();
            }}
          >
            Mark pending paid
          </button>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          disabled={!availableWinIds.length}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => {
            setSelected(availableWinIds);
            setBoxError("");
          }}
        >
          Select all unboxed wins
        </button>
        <button
          disabled={!selected.length}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => {
            resetBoxForm();
          }}
        >
          {editingPackageId ? "Cancel edit" : "Clear selection"}
        </button>
      </div>
      <div className="mt-4 grid gap-2 md:grid-cols-4 lg:grid-cols-7">
        <Metric compact label="Units" value={totals.totalUnits} />
        <Metric
          compact
          label="Revenue"
          value={formatAuctionMoney(totals.grossRevenueCents)}
        />
        <Metric
          compact
          label="Buyer paid ship"
          value={formatAuctionMoney(totals.buyerPaidShippingCents)}
        />
        <Metric
          compact
          label="Seller covers"
          value={formatAuctionMoney(totals.sellerCoveredShippingCents)}
        />
        <Metric
          compact
          label="COGS"
          value={formatAuctionMoney(totals.cogsCents)}
        />
        <Metric
          compact
          label="Fees"
          value={formatAuctionMoney(totals.platformFeesCents)}
        />
        <Metric
          compact
          label="Profit"
          value={formatAuctionMoney(totals.netProfitCents)}
        />
      </div>
      <div className="mt-4 grid gap-2 md:grid-cols-2">
        {wins.map((w) => (
          <label
            key={w._id}
            className={`flex justify-between rounded-lg border p-3 ${assigned.has(w._id) ? "bg-slate-50 text-slate-400" : ""}`}
          >
            <span>
              <div>
                <input
                  type="checkbox"
                  className="mr-2"
                  disabled={assigned.has(w._id)}
                  checked={selected.includes(w._id)}
                  onChange={(e) =>
                    setSelected((v) =>
                      e.target.checked
                        ? [...v, w._id]
                        : v.filter((id) => id !== w._id),
                    )
                  }
                  onClick={() => setBoxError("")}
                />
                {w.inventoryItemNameSnapshot} × {w.quantity}
              </div>
              <small className="mt-1 block text-slate-500">
                Cost {formatAuctionMoney(w.costPerUnitCents * w.quantity)} · Fee{" "}
                {formatAuctionMoney(w.platformFeeCents)} · Profit before box
                costs {formatAuctionMoney(lineProfitBeforeBoxCosts(w))}
              </small>
            </span>
            <span>
              {formatAuctionMoney(w.winningPriceCents * w.quantity)} ·{" "}
              {w.status}
              <button
                type="button"
                className="ml-3 font-semibold text-blue-700"
                onClick={() => {
                  setEditingWinId(w._id);
                  setEditingWinItem(w.inventoryItemNameSnapshot);
                  setEditingWinItemId(w.inventoryItemId || "");
                  setEditingWinPrice(String(w.winningPriceCents / 100));
                  setEditingWinQuantity(String(w.quantity));
                }}
              >
                Edit win
              </button>
            </span>
          </label>
        ))}
      </div>
      {editingWinId && (
        <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 p-4">
          <h3 className="font-bold">Edit win</h3>
          <div className="mt-3 grid gap-3 md:grid-cols-5">
            <label className="text-sm font-semibold md:col-span-2">
              Inventory item
              <input
                list="buyer-edit-inventory"
                className={`${input} mt-1`}
                placeholder="Search replacement inventory item"
                value={editingWinItem}
                onChange={(event) => {
                  setEditingWinItem(event.target.value);
                  const exactItem = inventory.find(
                    (item) =>
                      item.itemName.toLowerCase() ===
                        event.target.value.trim().toLowerCase() ||
                      item.sku?.toLowerCase() ===
                        event.target.value.trim().toLowerCase(),
                  );
                  setEditingWinItemId(exactItem?._id || "");
                }}
              />
              <datalist id="buyer-edit-inventory">
                {inventory.map((item) => (
                  <option key={item._id} value={item.itemName}>
                    {item.sku}
                  </option>
                ))}
              </datalist>
            </label>
            <label className="text-sm font-semibold">
              Winning price
              <input
                type="number"
                min="0"
                step="1"
                inputMode="numeric"
                className={`${input} mt-1`}
                placeholder="Winning price"
                value={editingWinPrice}
                onChange={(event) => setEditingWinPrice(event.target.value)}
              />
            </label>
            <label className="text-sm font-semibold">
              Quantity
              <input
                type="number"
                min="1"
                step="1"
                inputMode="numeric"
                className={`${input} mt-1`}
                placeholder="Quantity"
                value={editingWinQuantity}
                onChange={(event) => setEditingWinQuantity(event.target.value)}
              />
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                className={primary}
                onClick={() => void saveWinEdit()}
              >
                Save win
              </button>
              <button
                type="button"
                className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700"
                onClick={resetWinEdit}
              >
                Cancel
              </button>
            </div>
          </div>
          {selectedInventoryItem && (
            <p className="mt-2 text-sm text-slate-600">
              Selected inventory:{" "}
              <strong>{selectedInventoryItem.itemName}</strong>
              {selectedInventoryItem.sku
                ? ` · SKU ${selectedInventoryItem.sku}`
                : ""}
              {" · "}Current cost{" "}
              {formatAuctionMoney(toCents(selectedInventoryItem.costPerUnit))}
            </p>
          )}
        </div>
      )}
      <div className="mt-4 grid gap-3 md:grid-cols-9">
        <select
          aria-label="Saved box presets"
          className={input}
          value={selectedBoxPresetId}
          onChange={(e) => {
            const id = e.target.value;
            setSelectedBoxPresetId(id);
            const preset = boxPresets.find((row) => row._id === id);
            if (!preset) return;
            setBoxSizeLabel(preset.label);
            setPackaging((preset.packagingCostCents / 100).toFixed(2));
          }}
        >
          <option value="">Saved box sizes</option>
          {boxPresets.map((preset) => (
            <option key={preset._id} value={preset._id}>
              {preset.label} · {formatAuctionMoney(preset.packagingCostCents)}
            </option>
          ))}
        </select>
        <input
          className={input}
          placeholder="Box size (e.g. 4x4x4)"
          value={boxSizeLabel}
          onChange={(e) => {
            setBoxSizeLabel(e.target.value);
            if (
              selectedBoxPresetId &&
              boxPresets.find((row) => row._id === selectedBoxPresetId)
                ?.label !== e.target.value
            ) {
              setSelectedBoxPresetId("");
            }
          }}
        />
        <input
          className={input}
          type="number"
          min="0"
          step=".01"
          placeholder="Box packaging $"
          value={packaging}
          onChange={(e) => setPackaging(e.target.value)}
        />
        <input
          className={input}
          type="number"
          min="0"
          step=".01"
          placeholder="Actual shipping $"
          value={shipping}
          onChange={(e) => setShipping(e.target.value)}
        />
        <input
          className={input}
          type="number"
          min="0"
          step=".01"
          placeholder="Buyer paid shipping $"
          value={income}
          onChange={(e) => setIncome(e.target.value)}
        />
        <input
          className={input}
          type="number"
          min="0"
          step=".01"
          placeholder="Platform credit $"
          value={credit}
          onChange={(e) => setCredit(e.target.value)}
        />
        <input
          className={input}
          placeholder="TikTok order ID"
          value={order}
          onChange={(e) => setOrder(e.target.value)}
        />
        <input
          className={input}
          placeholder="Tracking"
          value={tracking}
          onChange={(e) => setTracking(e.target.value)}
        />
        <button
          type="button"
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700"
          disabled={!canSaveBoxPreset}
          onClick={() => void saveBoxPreset()}
        >
          Save preset
        </button>
        <button
          type="button"
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!selectedBoxPresetId}
          onClick={() => void removeBoxPreset()}
        >
          Delete preset
        </button>
        <button
          disabled={!canCreateBox}
          className={primary}
          onClick={() => void saveBox()}
        >
          {editingPackageId ? "Update box" : "Create box"}
        </button>
      </div>
      {boxError && (
        <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {boxError}
        </p>
      )}
      {!canCreateBox && (
        <p className="mt-2 text-sm text-slate-500">
          Check at least one won item before creating a box.
        </p>
      )}
      {editingPackageId && (
        <p className="mt-2 text-sm font-semibold text-blue-700">
          Editing an existing box. You can change both the shipping numbers and
          which wins are included.
        </p>
      )}
      <p className="mt-2 text-sm text-slate-500">
        Each box can hold any subset of this buyer&apos;s won items. If the
        buyer pays part of shipping and you cover the rest, enter the actual
        shipping cost and the amount the buyer paid.
      </p>
      {packages.map((p, i) => (
        <div key={p._id} className="mt-3 rounded-lg bg-slate-50 p-3 text-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <strong>Box {i + 1}</strong>
                {p.boxSizeLabel ? <span>({p.boxSizeLabel})</span> : null}
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                    p.status === "SHIPPED"
                      ? "bg-emerald-100 text-emerald-700"
                      : p.status === "DELIVERED"
                        ? "bg-blue-100 text-blue-700"
                        : p.status === "CANCELLED"
                          ? "bg-red-100 text-red-700"
                          : p.status === "READY"
                            ? "bg-amber-100 text-amber-700"
                            : "bg-slate-200 text-slate-700"
                  }`}
                >
                  {p.status}
                </span>
                {p.trackingNumber ? (
                  <span className="text-slate-500">Tracking {p.trackingNumber}</span>
                ) : null}
              </div>
              <div className="mt-1 text-slate-700">
                {packageUnits(p, wins)} units · {p.auctionWinIds.length} win lines
                · Packaging {formatAuctionMoney(p.packagingCostCents)} · Actual
                ship {formatAuctionMoney(p.actualShippingCostCents)} · Buyer paid{" "}
                {formatAuctionMoney(
                  p.customerPaidShippingCents + p.platformShippingCreditCents,
                )}{" "}
                · Seller covers {formatAuctionMoney(packageSellerCovered(p))}{" "}
                {p.tiktokOrderId && `· Order ${p.tiktokOrderId}`}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {p.status !== "SHIPPED" && p.status !== "DELIVERED" && p.status !== "CANCELLED" ? (
                <button
                  type="button"
                  className="rounded-lg border border-emerald-300 px-3 py-1.5 text-sm font-semibold text-emerald-700"
                  onClick={async () => {
                    await liveAuctionsApi.updatePackage(p._id, {
                      status: "SHIPPED",
                    });
                    await refresh();
                  }}
                >
                  Mark shipped
                </button>
              ) : null}
              <button
                className="font-semibold text-blue-700"
                onClick={() => {
                  setEditingPackageId(p._id);
                  setSelected(p.auctionWinIds);
                  setBoxSizeLabel(p.boxSizeLabel || "");
                  const matchedPreset = boxPresets.find(
                    (preset) => preset.label === (p.boxSizeLabel || ""),
                  );
                  setSelectedBoxPresetId(matchedPreset?._id || "");
                  setPackaging((p.packagingCostCents / 100).toFixed(2));
                  setShipping((p.actualShippingCostCents / 100).toFixed(2));
                  setIncome((p.customerPaidShippingCents / 100).toFixed(2));
                  setCredit((p.platformShippingCreditCents / 100).toFixed(2));
                  setOrder(p.tiktokOrderId || "");
                  setTracking(p.trackingNumber || "");
                  setBoxError("");
                }}
              >
                Edit
              </button>
              <select
                aria-label={`Box ${i + 1} status`}
                className="rounded border border-slate-300 bg-white px-2 py-1"
                value={p.status}
                onChange={async (event) => {
                  await liveAuctionsApi.updatePackage(p._id, {
                    status: event.target.value,
                  });
                  await refresh();
                }}
              >
                <option>DRAFT</option>
                <option>READY</option>
                <option>SHIPPED</option>
                <option>DELIVERED</option>
                <option>CANCELLED</option>
              </select>
            </div>
          </div>
        </div>
      ))}
    </section>
  );
}
